import { ActionTypes } from '../actions/action-types'
import { queryArrayToKeys, toggleFilterItem } from '../utils'
import {
  defaultDistance,
  defaultLeaseFinanceRange,
  defaultLocation,
  defaultMaxMileageValue,
  defaultMileageRange,
  defaultPriceRange,
  defaultYearRange,
  ListingViewType,
} from '../config'

// ----------------------------------------------------------------------

const defaultListingState = {
  vehicles: { byId: {}, allIds: [] },
  additionalInfo: {},
  totalCount: 0,
  totalUnrestrictedCount: 0,
  lastPageFetched: false,
}

export const defaultFiltersState = {
  price: {
    start: defaultPriceRange.start,
    end: defaultPriceRange.end,
  },
  leaseFinance: {
    start: defaultLeaseFinanceRange.start,
    end: defaultLeaseFinanceRange.end,
  },
  makes: {},
  models: {},
  bodyStyle: [],
  feature: [],
  year: {
    start: defaultYearRange.start,
    end: defaultYearRange.end,
  },
  mileage: {
    start: defaultMileageRange.start,
    end: defaultMileageRange.end,
  },
  maxMileage: defaultMaxMileageValue,
  fuel: [],
  engineSize: [],
  transmission: [],
  drivetrain: [],
  colour: [],
  membership: {},
  location: defaultLocation,
  distance: defaultDistance,
  listingType: {},
  searchTerms: '',
  sortType: { fieldName: 'auctionTime', sortOrder: 'Ascending' },
}

// ----------------------------------------------------------------------

export const getInitialState = ({ query, userDetails, userLocation, authRedirect }) => {
  return {
    // Filter State
    price: {
      start: query.priceStart || defaultPriceRange.start,
      end: query.priceEnd || defaultPriceRange.end,
    },
    leaseFinance: {
      start: query.leaseFinanceStart || defaultLeaseFinanceRange.start,
      end: query.leaseFinanceEnd || defaultLeaseFinanceRange.end,
    },
    makes: JSON.parse(query.makes || '{}'),
    models: JSON.parse(query.models || '{}'),
    bodyStyle: queryArrayToKeys(query.bodyStyle || []),
    feature: queryArrayToKeys(query.feature || []),
    year: {
      start: query.yearStart || defaultYearRange.start,
      end: query.yearEnd || defaultYearRange.end,
    },
    mileage: {
      start: query.mileageStart || defaultMileageRange.start,
      end: query.mileageEnd || defaultMileageRange.end,
    },
    maxMileage: defaultMaxMileageValue,
    fuel: queryArrayToKeys(query.fuel || []),
    engineSize: queryArrayToKeys(query.engineSize || []),
    transmission: queryArrayToKeys(query.transmission || []),
    drivetrain: queryArrayToKeys(query.drivetrain || []),
    colour: queryArrayToKeys(query.colour || []),
    membership: queryArrayToKeys(query.membership || []),
    location: {
      zipCode:
        query.zipCode || userDetails?.zipCode || userLocation?.zipCode || defaultLocation.zipCode,
      latitude:
        (query.latitude && +query.latitude) ||
        userDetails?.location?.lat ||
        userLocation?.latitude ||
        defaultLocation.latitude,
      longitude:
        (query.longitude && +query.longitude) ||
        userDetails?.location?.lng ||
        userLocation?.longitude ||
        defaultLocation.longitude,
    },
    distance: +query.distance || defaultDistance,
    listingType: queryArrayToKeys(query.listingType || []),
    searchTerms: query.searchTerms,
    viewType: +query.viewType || ListingViewType.Grid,
    pageIndex: 0,
    authRedirect: authRedirect,
    errors: {
      priceStart: null,
      priceEnd: null,
    },
    sortType: {
      fieldName: query.fieldName || defaultFiltersState.sortType.fieldName,
      sortOrder: query.sortOrder || defaultFiltersState.sortType.sortOrder,
    },

    // Listing state
    listing: defaultListingState,
  }
}

// ----------------------------------------------------------------------

export default function reducer(state, action) {
  switch (action.type) {
    case ActionTypes.setPrice:
      return {
        ...state,
        price: action.payload,
        errors: {
          ...state.errors,
          priceStart: action.payload.start ? null : 'Required',
          priceEnd: action.payload.end ? null : 'Required',
        },
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setLeaseFinance:
      return {
        ...state,
        leaseFinance: action.payload,
        errors: {
          ...state.errors,
          leaseFinanceStart: action.payload.start ? null : 'Required',
          leaseFinanceEnd: action.payload.end ? null : 'Required',
        },
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setMakes: {
      if (state.makes[action.payload.id]) {
        return state
      }

      return {
        ...state,
        makes: { ...state.makes, [action.payload.id]: action.payload },
        pageIndex: 0,
        listing: defaultListingState,
      }
    }

    case ActionTypes.setModels: {
      const clonedModels = { ...state.models }
      const modelName = action.payload.name.toLowerCase()
      if (clonedModels[modelName]) delete clonedModels[modelName]
      else Object.assign(clonedModels, { [modelName]: action.payload })

      return { ...state, models: clonedModels, pageIndex: 0, listing: defaultListingState }
    }

    case ActionTypes.setSortType: {
      function isSortingChanged() {
        return (
          state.sortType.fieldName !== action.payload.fieldName ||
          state.sortType.sortOrder !== action.payload.sortOrder
        )
      }

      return {
        ...state,
        ...(isSortingChanged() && {
          sortType: action.payload,
          pageIndex: 0,
          listing: defaultListingState,
        }),
      }
    }

    case ActionTypes.setAllModels:
      return {
        ...state,
        models: action.payload.reduce((acc, cur) => {
          const key = cur.name.toLowerCase()
          return { ...acc, [key]: { id: cur.id, name: cur.name } }
        }, {}),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setBodyStyle:
      return {
        ...state,
        bodyStyle: toggleFilterItem(action.payload, state.bodyStyle),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setFeature:
      return {
        ...state,
        feature: toggleFilterItem(action.payload, state.feature),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setYear:
      return {
        ...state,
        year: action.payload,
        errors: {
          ...state.errors,
          yearStart: action.payload.start ? null : 'Required',
          yearEnd: action.payload.end ? null : 'Required',
        },
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setMileage:
      return {
        ...state,
        mileage: action.payload,
        errors: {
          ...state.errors,
          mileageStart: action.payload.start ? null : 'Required',
          mileageEnd: action.payload.end ? null : 'Required',
        },
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setMaxMileage:
      return {
        ...state,
        maxMileage: action.payload,
        mileage: {
          ...state.mileage,
          end:
            Number(state.mileage.end) > Number(action.payload) ? action.payload : state.mileage.end,
        },
      }

    case ActionTypes.setFuel:
      return {
        ...state,
        fuel: toggleFilterItem(action.payload, state.fuel),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setEngineSize:
      return {
        ...state,
        engineSize: toggleFilterItem(action.payload, state.engineSize),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setTransmission:
      return {
        ...state,
        transmission: toggleFilterItem(action.payload, state.transmission),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setDrivetrain:
      return {
        ...state,
        drivetrain: toggleFilterItem(action.payload, state.drivetrain),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setColour:
      return {
        ...state,
        colour: toggleFilterItem(action.payload, state.colour),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setMembership:
      return {
        ...state,
        membership: toggleFilterItem(action.payload, state.membership),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.setLocation:
      return { ...state, location: action.payload, pageIndex: 0, listing: defaultListingState }

    case ActionTypes.setDistance:
      return { ...state, distance: action.payload, pageIndex: 0, listing: defaultListingState }

    case ActionTypes.setListingType: {
      return {
        ...state,
        listingType: toggleFilterItem(action.payload, state.listingType),
        pageIndex: 0,
        listing: defaultListingState,
      }
    }

    case ActionTypes.setSearchTerms:
      return {
        ...state,
        searchTerms: decodeURIComponent(action.payload),
        pageIndex: 0,
        listing: defaultListingState,
      }

    case ActionTypes.removeTag: {
      if (action.name === 'searchTerms')
        return { ...state, searchTerms: null, pageIndex: 0, listing: defaultListingState }

      if (action.name === 'price')
        return {
          ...state,
          price: defaultPriceRange,
          errors: { ...state.errors, priceStart: null, priceEnd: null },
          pageIndex: 0,
          listing: defaultListingState,
        }

      if (action.name === 'leaseFinance')
        return {
          ...state,
          leaseFinance: defaultLeaseFinanceRange,
          errors: { ...state.errors, leaseFinanceStart: null, leaseFinanceEnd: null },
          pageIndex: 0,
          listing: defaultListingState,
        }

      if (action.name === 'year')
        return {
          ...state,
          year: defaultYearRange,
          errors: { ...state.errors, yearStart: null, yearEnd: null },
          pageIndex: 0,
          listing: defaultListingState,
        }

      if (action.name === 'mileage')
        return {
          ...state,
          mileage: defaultMileageRange,
          errors: { ...state.errors, mileageStart: null, mileageEnd: null },
          pageIndex: 0,
          listing: defaultListingState,
        }

      const clonedField = state[action.name]
      delete clonedField[action.value]
      return { ...state, [action.name]: clonedField, pageIndex: 0, listing: defaultListingState }
    }

    case ActionTypes.setViewType:
      return { ...state, viewType: action.payload }

    case ActionTypes.setPageIndex:
      return { ...state, pageIndex: action.payload }

    case ActionTypes.clearFilters:
      return { ...state, ...defaultFiltersState, pageIndex: 0, listing: defaultListingState }

    case ActionTypes.setListingVehicles: {
      const vehicleIds = action.payload.vehicles?.map((vehicle) => vehicle.id)
      const vehiclesMap =
        action.payload?.vehicles?.reduce(
          (acc, current) => ({ ...acc, [current.id]: current }),
          {},
        ) || {}

      return {
        ...state,
        listing: {
          ...state.listing,
          totalCount: action.payload.totalCount,
          totalUnrestrictedCount: action.payload.totalUnrestrictedCount,
          vehicles: {
            byId: {
              ...state.listing.vehicles.byId,
              ...vehiclesMap,
            },
            allIds: [...new Set(state.listing.vehicles.allIds.concat(vehicleIds))],
          },
        },
      }
    }

    case ActionTypes.setListingAdditionalInfo: {
      const normalizedVehiclesInfo =
        action.payload.vehicles?.reduce(
          (acc, current) => ({ ...acc, [current.id]: current }),
          {},
        ) || {}

      return {
        ...state,
        listing: {
          ...state.listing,
          additionalInfo: { ...state.listing.additionalInfo, normalizedVehiclesInfo },
        },
      }
    }

    default:
      return state
  }
}
